// "Generate Test Cases" — a standalone, self-contained flow (Workspace → Generate
// Test Cases). Fully decoupled from the (paused) Projects/ProjectDetails surface.
//
//   LIST view   → search + filter a grid of projects, or create a new one.
//   DETAIL view → add role-based logins (the gate), generate AI test cases, then
//                 browse the pages found + generated cases (read-only, filterable).
//
// UI/flow only — every call reuses an existing wrapper in api/projects.js; no
// backend behaviour changes.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import {
  Sparkles, Loader2, Download, RefreshCw, FileText, Layers, DollarSign, Clock,
  ArrowRight, ArrowLeft, FolderKanban, Plus, Globe, KeyRound, Pencil,
  Trash2, AlertTriangle, Search, ShieldAlert, FolderSearch,
  AtSign, Filter, Code2, Bot, Puzzle, FilePlus2,
  Pause, Play, Square, Wand2, ListPlus,
} from "lucide-react";
import Aurora from "@/components/common/Aurora";
import Button from "@/components/ui/Button";
import Input from "@/components/ui/Input";
import Modal from "@/components/dashboard/Modal";
import EmptyState from "@/components/shared/EmptyState";
import ProjectCasesCard from "@/components/dashboard/ProjectCasesCard";
import RepositoryConnections from "@/components/dashboard/RepositoryConnections";
import BatchProgress from "@/components/dashboard/BatchProgress";
import CrawledPagesList from "@/components/dashboard/CrawledPagesList";
import { cn } from "@/lib/cn";
import {
  getProjects, pageProjectTestCases, getTestCaseVersions,
  getTestCaseSheets, getPages,
  generateTests, getWorkflow, downloadProjectTestCases, downloadTestCasesVersion,
  rollbackTestCases,
  addCredential, getCredentials, updateCredential, deleteCredential, deleteProject,
  getProjectAssignees, getProjectRepositories,
  pushToExe, pushToExtension, generateTestsForPage,
  cancelProject, pauseProject, resumeProject,
  getQueuedPages, generateQueuedPages,
} from "@/api/projects";
import { toastError, toastSuccess } from "@/lib/toast";

// ---------------------------------------------------------------------------
// Formatting helpers
// ---------------------------------------------------------------------------
const fmtCost = (n) => {
  const v = Number(n);
  if (!isFinite(v) || v <= 0) return "$0.00";
  if (v < 0.01) return "<$0.01";
  return `$${v.toFixed(2)}`;
};
const fmtTime = (s) => {
  s = Math.round(Number(s || 0));
  if (s <= 0) return "—";
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  return `${m}m ${s % 60}s`;
};

// Role-based login model — mirrors the previous ProjectDetails credential form
// (role + optional login URL + the custom-username-field override).
const EMPTY_CRED = {
  label: "", role: "", login_url: "", username: "", password: "",
  is_default: false,
  username_field_override_enabled: false,
  username_field_override: "",
};


// ===========================================================================
// Top-level: owns the project list + create modal, and switches to the
// workspace when a project is selected.
// ===========================================================================
export default function GenerateTests() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);

  // list controls
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all"); // all | has | none
  const [sort, setSort] = useState("name");     // name | recent
  const [running, setRunning] = useState({});   // projectId -> live workflow

  // Project CREATION moved to the project manager (/pm). A tester works on
  // what they're assigned; they add the repository and logins here, then
  // generate. That's why there's no create form on this page any more.

  const loadProjects = useCallback(async () => {
    try {
      // The list carries its own test-case counts now (one grouped query on the
      // server). This used to fetch /test-cases once PER PROJECT just to read a
      // total off each response — twenty projects meant twenty-one requests, and
      // each of those responses was the project's entire case list.
      const list = await getProjects();
      const rows = (list || []).map((p) => ({ ...p, total_tests: p.test_cases ?? 0 }));
      setProjects(rows);
      return rows;
    } catch (e) {
      toastError(e.message || "Failed to load projects.");
      return [];
    }
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await loadProjects();
      setLoading(false);
    })();
  }, [loadProjects]);

  // Which projects are generating RIGHT NOW ({ [projectId]: workflow }).
  //
  // Generation happens on the server, so a project can be mid-crawl while this
  // list is open — or while the tester is on another tab entirely. We ask once on
  // load, then keep polling ONLY the projects that came back "running" (usually
  // zero or one), so the list stays honest without hammering the API.
  const checkRunning = useCallback(async (list) => {
    const projects = list || [];
    if (!projects.length) return;
    const results = await Promise.allSettled(projects.map((p) => getWorkflow(p.id)));
    const live = {};
    results.forEach((r, i) => {
      if (r.status === "fulfilled" && r.value?.state === "running") {
        live[projects[i].id] = r.value;
      }
    });
    setRunning(live);
  }, []);

  // Re-sync whenever we land back on the LIST. This component never unmounts when
  // you open a project, so without this the map would still hold whatever was true
  // before you went in — and a run you just STARTED inside a project wouldn't show
  // on its card when you came back out.
  useEffect(() => {
    if (selectedId || !projects.length) return;
    checkRunning(projects);
    // Only when we arrive on the list or the project set changes — not on every
    // unrelated re-render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, projects.length, checkRunning]);

  // Poll just the live ones; when one finishes, refresh its totals so the card's
  // "Test cases" count updates the moment generation completes.
  useEffect(() => {
    const ids = Object.keys(running);
    if (!ids.length) return undefined;
    let cancelled = false;
    const t = setTimeout(async () => {
      const results = await Promise.allSettled(ids.map((id) => getWorkflow(id)));
      if (cancelled) return;
      const still = {};
      let anyFinished = false;
      results.forEach((r, i) => {
        if (r.status === "fulfilled" && r.value?.state === "running") still[ids[i]] = r.value;
        else anyFinished = true;
      });
      setRunning(still);
      if (anyFinished) loadProjects();
    }, 3000);
    return () => { cancelled = true; clearTimeout(t); };
  }, [running, loadProjects]);

  // Refresh a single project's totals in the list (after generate/delete in the
  // workspace) without a full refetch storm.
  const patchProject = useCallback((id, patch) => {
    setProjects((prev) => prev.map((p) => (p.id === id ? { ...p, ...patch } : p)));
  }, []);

  // Stable callback handed to the workspace so its data-loading effect doesn't
  // re-run every time the list re-renders (an unstable inline fn here caused an
  // infinite load → setProjects → re-render → load loop). Identity only changes
  // when the selected project changes, at which point the workspace remounts
  // anyway (key={selectedId}), so this is safe.
  const handleTotals = useCallback((total, generation) => {
    if (selectedId) patchProject(selectedId, { total_tests: total, generation });
  }, [selectedId, patchProject]);

  const handleDeleted = useCallback(async () => {
    setSelectedId(null);
    setLoading(true);
    await loadProjects();
    setLoading(false);
  }, [loadProjects]);

  // Stable identity — an inline arrow here would change every render and
  // restart the workspace's loader in a loop (this bit us before).
  const handleSaved = useCallback(() => { loadProjects(); }, [loadProjects]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = projects.filter((p) => {
      if (filter === "has" && !(p.total_tests > 0)) return false;
      if (filter === "none" && p.total_tests > 0) return false;
      if (!q) return true;
      return (
        String(p.name || "").toLowerCase().includes(q) ||
        String(p.base_url || "").toLowerCase().includes(q)
      );
    });
    list = [...list].sort((a, b) => {
      if (sort === "name") return String(a.name).localeCompare(String(b.name));
      // recent — best-effort by created_at when present, else keep order
      return String(b.created_at || "").localeCompare(String(a.created_at || ""));
    });
    return list;
  }, [projects, query, filter, sort]);

  if (selectedId) {
    return (
      <ProjectWorkspace
        key={selectedId}
        projectId={selectedId}
        project={projects.find((p) => p.id === selectedId) || null}
        onBack={() => setSelectedId(null)}
        onTotals={handleTotals}
        onDeleted={handleDeleted}
        onSaved={handleSaved}
      />
    );
  }

  return (
    <>
      {/* hero */}
      <motion.div
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
        className="relative mb-6 overflow-hidden rounded-2xl border border-slate-200 bg-white p-6 shadow-soft dark:border-white/10 dark:bg-ink-900 sm:p-7"
      >
        <Aurora className="opacity-60" />
        <div className="relative flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3">
            <span className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl bg-gradient-to-br from-brand-500 to-fuchsia-500 text-white shadow-glow">
              <Sparkles size={20} />
            </span>
            <div>
              <p className="text-[0.6875rem] font-semibold uppercase tracking-wider text-brand-600 dark:text-brand-300">AI test authoring</p>
              <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900 dark:text-white sm:text-3xl">Generate Test Cases</h1>
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                Add the repository and logins to a project you&apos;ve been assigned, then
                generate AI test cases from a live crawl.
              </p>
            </div>
          </div>
        </div>
      </motion.div>

      {/* search + filters */}
      {!loading && projects.length > 0 && (
        <div className="mb-5 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="relative w-full lg:max-w-md">
            <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search projects by name or URL…"
              className="w-full rounded-xl border border-slate-300 bg-white py-2.5 pl-9 pr-3 text-sm text-slate-900 placeholder:text-slate-400 focus:border-brand-500 focus:outline-none focus:ring-4 focus:ring-brand-500/15 dark:border-white/10 dark:bg-white/5 dark:text-white"
            />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="hidden items-center gap-1 text-xs text-slate-400 sm:inline-flex"><Filter size={13} /> Filter</span>
            {[
              { key: "all", label: "All" },
              { key: "has", label: "Has test cases" },
              { key: "none", label: "Not generated" },
            ].map((o) => (
              <button
                key={o.key}
                onClick={() => setFilter(o.key)}
                className={cn(
                  "rounded-full px-3 py-1.5 text-xs font-medium transition-colors",
                  filter === o.key
                    ? "bg-brand-600 text-white shadow-soft"
                    : "border border-slate-200 text-slate-500 hover:bg-slate-100 dark:border-white/10 dark:text-slate-400 dark:hover:bg-white/5"
                )}
              >
                {o.label}
              </button>
            ))}
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-500 dark:border-white/10 dark:bg-white/5 dark:text-slate-300"
            >
              <option value="name">Sort: Name</option>
              <option value="recent">Sort: Newest</option>
            </select>
          </div>
        </div>
      )}

      {/* project grid */}
      {loading ? (
        <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
          {[0, 1, 2].map((i) => <ProjectCardSkeleton key={i} />)}
        </div>
      ) : projects.length === 0 ? (
        <EmptyState
          icon={FolderKanban}
          title="No projects assigned yet"
          message="Your project manager assigns you the apps you'll test. Once one lands here, add its repository and logins and generate the test cases."
        />
      ) : filtered.length === 0 ? (
        <div className="card flex flex-col items-center gap-2 p-10 text-center">
          <FolderSearch className="text-slate-300" size={34} />
          <p className="text-sm text-slate-500 dark:text-slate-400">No projects match your search or filter.</p>
          <button onClick={() => { setQuery(""); setFilter("all"); }} className="text-sm font-medium text-brand-600 hover:text-brand-500 dark:text-brand-400">Clear filters</button>
        </div>
      ) : (
        <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
          {filtered.map((p, i) => (
            <ProjectCard key={p.id} project={p} index={i} workflow={running[p.id]}
              onOpen={() => setSelectedId(p.id)} />
          ))}
        </div>
      )}

    </>
  );
}

// ---------------------------------------------------------------------------
// List: project card + skeleton
// ---------------------------------------------------------------------------
function ProjectCardSkeleton() {
  return (
    <div className="card p-5">
      <div className="flex items-center gap-2.5">
        <div className="h-10 w-10 animate-pulse rounded-xl bg-slate-200 dark:bg-white/10" />
        <div className="h-4 w-32 animate-pulse rounded bg-slate-200 dark:bg-white/10" />
      </div>
      <div className="mt-3 h-3 w-44 animate-pulse rounded bg-slate-200 dark:bg-white/10" />
      <div className="mt-4 grid grid-cols-2 gap-3">
        <div className="h-16 animate-pulse rounded-xl bg-slate-100 dark:bg-white/5" />
        <div className="h-16 animate-pulse rounded-xl bg-slate-100 dark:bg-white/5" />
      </div>
      <div className="mt-5 h-9 animate-pulse rounded-xl bg-slate-100 dark:bg-white/5" />
    </div>
  );
}

// `workflow` is set only while this project is generating on the server, so the
// card can show live progress even if the tester started it, navigated away, and
// came back — or never opened the project at all.
function ProjectCard({ project: p, index, onOpen, workflow }) {
  const gen = p.generation || null;
  const live = workflow?.state === "running";
  const isPaused = !!workflow?.paused;
  return (
    <motion.button
      type="button"
      onClick={onOpen}
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: Math.min(index * 0.05, 0.4), duration: 0.4, ease: "easeOut" }}
      whileHover={{ y: -4 }}
      className="card group relative flex flex-col overflow-hidden p-5 text-left"
    >
      <div className="pointer-events-none absolute -right-10 -top-10 h-28 w-28 rounded-full bg-brand-500/10 opacity-0 blur-2xl transition-opacity duration-300 group-hover:opacity-100" />
      <div className="relative flex items-start justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2.5">
          <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-gradient-to-br from-brand-500/15 to-fuchsia-500/15 text-brand-600 transition-transform duration-200 group-hover:scale-105 dark:text-brand-300">
            <FolderKanban size={20} />
          </span>
          <h3 className="truncate font-semibold text-slate-900 transition-colors group-hover:text-brand-600 dark:text-white dark:group-hover:text-brand-300">{p.name}</h3>
        </div>
        {live ? (
          isPaused ? (
            <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-semibold text-amber-700 dark:bg-amber-500/15 dark:text-amber-300">
              <Pause size={11} /> Paused
            </span>
          ) : (
            <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-brand-100 px-2.5 py-0.5 text-xs font-semibold text-brand-700 dark:bg-brand-500/15 dark:text-brand-300">
              <Loader2 size={11} className="animate-spin" /> Generating
            </span>
          )
        ) : p.total_tests > 0 ? (
          <span className="shrink-0 rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-600 dark:bg-emerald-500/10 dark:text-emerald-400">{p.total_tests} cases</span>
        ) : (
          <span className="shrink-0 rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-400 dark:bg-white/10">Not generated</span>
        )}
      </div>

      <p className="relative mt-3 flex items-center gap-1.5 truncate text-sm text-slate-500 dark:text-slate-400">
        <Globe size={14} className="shrink-0" /> <span className="truncate">{p.base_url}</span>
      </p>

      {/* Live phase + message, so the list says WHAT is happening, not just that
          something is. */}
      {live && (
        <div className={cn("relative mt-3 rounded-xl border px-3 py-2",
          isPaused
            ? "border-amber-200 bg-amber-50/70 dark:border-amber-500/20 dark:bg-amber-500/10"
            : "border-brand-200 bg-brand-50/70 dark:border-brand-500/20 dark:bg-brand-500/10")}>
          <p className={cn("flex items-center gap-1.5 text-xs font-medium capitalize",
            isPaused ? "text-amber-800 dark:text-amber-200" : "text-brand-700 dark:text-brand-200")}>
            {isPaused
              ? <><Pause size={12} /> Paused</>
              : <><Loader2 size={12} className="animate-spin" /> {workflow.phase || "working"}</>}
          </p>
          {!isPaused && workflow.message && (
            <p className="mt-0.5 truncate text-[0.6875rem] text-brand-600/80 dark:text-brand-300/80">{workflow.message}</p>
          )}
          <div className={cn("mt-2 h-1 w-full overflow-hidden rounded-full",
            isPaused ? "bg-amber-200/60 dark:bg-amber-500/20" : "bg-brand-200/60 dark:bg-brand-500/20")}>
            {isPaused ? (
              <div className="h-full w-1/3 rounded-full bg-amber-400/70" />
            ) : (
              <motion.div
                className="h-full w-1/3 rounded-full bg-gradient-to-r from-brand-500 to-fuchsia-500"
                animate={{ x: ["-110%", "330%"] }}
                transition={{ repeat: Infinity, duration: 1.4, ease: "easeInOut" }}
              />
            )}
          </div>
        </div>
      )}

      <div className="relative mt-4 grid grid-cols-2 gap-3">
        <div className="rounded-xl bg-slate-50 px-3 py-2.5 dark:bg-white/5">
          <p className="flex items-center gap-1.5 text-xs text-slate-400"><FileText size={12} /> Test cases</p>
          <p className="mt-0.5 text-lg font-bold text-slate-900 dark:text-white">{p.total_tests || 0}</p>
        </div>
        <div className="rounded-xl bg-slate-50 px-3 py-2.5 dark:bg-white/5">
          <p className="flex items-center gap-1.5 text-xs text-slate-400"><Layers size={12} /> Version</p>
          <p className="mt-0.5 text-lg font-bold text-slate-900 dark:text-white">{gen ? `v${gen.version}` : "—"}</p>
        </div>
      </div>

      {gen && (gen.cost_usd > 0 || gen.duration_seconds > 0) && (
        <p className="relative mt-2 flex items-center gap-3 text-xs text-slate-400">
          <span className="inline-flex items-center gap-1"><Sparkles size={12} /> {fmtCost(gen.cost_usd)}</span>
          <span>·</span>
          <span>{fmtTime(gen.duration_seconds)}</span>
        </p>
      )}

      <div className="relative mt-5 border-t border-slate-100 pt-4 dark:border-white/10">
        <span className="inline-flex items-center gap-1 text-sm font-medium text-brand-600 group-hover:text-brand-500 dark:text-brand-400">
          {p.total_tests > 0 ? "Open" : "Generate"} <ArrowRight size={15} className="transition-transform group-hover:translate-x-0.5" />
        </span>
      </div>
    </motion.button>
  );
}

// "Test a single page" card remembers the last URL/login it ran with, per
// project, across reloads — see the pageUrl/pageAccount state in
// ProjectWorkspace for why.
const lastPageUrlKey = (projectId) => `aiqa:last-single-page-url:${projectId}`;
const lastPageAccountKey = (projectId) => `aiqa:last-single-page-account:${projectId}`;

/**
 * Why Generate isn't here.
 *
 * Shown in the button's place, not as a banner somewhere else on the page —
 * the answer has to be where the missing control was, or it reads as a bug.
 * `missing` is already a list of noun phrases ("a login", "a repository").
 */
function MissingGate({ missing }) {
  return (
    <p className="inline-flex items-center gap-1.5 rounded-lg bg-rose-50 px-3 py-2 text-xs font-medium text-rose-600 dark:bg-rose-500/10 dark:text-rose-300">
      <AlertTriangle size={13} className="shrink-0" />
      Add {missing.join(" and ")} below to generate test cases.
    </p>
  );
}

// ===========================================================================
// Workspace: one project — logins gate → generate → pages + cases (read-only).
// ===========================================================================
function ProjectWorkspace({ projectId, project, onBack, onDeleted, onTotals, onSaved }) {
  const [summary, setSummary] = useState(null); // { total, by_page, test_cases, generation }
  const [pages, setPages] = useState(null);
  const [versions, setVersions] = useState([]);
  const [credentials, setCredentials] = useState([]);
  const [repos, setRepos] = useState([]);
  const [loading, setLoading] = useState(true);

  const [busy, setBusy] = useState(false);
  const [workflow, setWorkflow] = useState(null);
  const [downloading, setDownloading] = useState(false);

  // credential modal
  const [credOpen, setCredOpen] = useState(false);
  const [credEditId, setCredEditId] = useState(null);
  const [credForm, setCredForm] = useState(EMPTY_CRED);
  const [credSaving, setCredSaving] = useState(false);
  const [credError, setCredError] = useState(null);

  // delete project
  const [confirmDel, setConfirmDel] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Who else is on this project. READ-ONLY here — staffing is the project
  // manager's call, so a tester sees the team but can't change it.
  const [roster, setRoster] = useState(null);
  const [pushing, setPushing] = useState(null); // "exe" | "extension" | null

  // single-page generate ("Test a single page" card). Persisted per project so
  // Excel/Send to Exe don't go dead on a reload: those buttons are scoped to
  // whatever URL is in this box (see downloadPage/sendPageToExe below), and a
  // reload used to reset it to empty even though the cases those buttons need
  // were already saved — disabling a working feature for no reason.
  const [pageUrl, setPageUrl] = useState(() => {
    try { return localStorage.getItem(lastPageUrlKey(projectId)) || ""; } catch { return ""; }
  });
  const [pageAccount, setPageAccount] = useState(() => {
    try { return localStorage.getItem(lastPageAccountKey(projectId)) || ""; } catch { return ""; }
  });
  const [pageDl, setPageDl] = useState(false);   // "Excel" in that same card
  const [pageSend, setPageSend] = useState(false); // "Send to Exe" in that same card

  useEffect(() => {
    try { localStorage.setItem(lastPageUrlKey(projectId), pageUrl); } catch { /* private mode etc — non-fatal */ }
  }, [projectId, pageUrl]);
  useEffect(() => {
    try { localStorage.setItem(lastPageAccountKey(projectId), pageAccount); } catch { /* ignore */ }
  }, [projectId, pageAccount]);
  // per-account "download test cases" (in each login row)
  const [rowDl, setRowDl] = useState(null); // credential label currently downloading
  // The tabs the exported workbook will contain, in workbook order. Shown before
  // the download so the role split is visible without opening the file.
  const [sheets, setSheets] = useState([]);
  // Pages a previous crawl discovered and never reached. Its own number, not one
  // derived from the page list: "already crawled" and "still owed a crawl" are
  // different questions and only the server can answer the second.
  const [queued, setQueued] = useState({ total: 0, accounts: [] });

  const polling = useRef(null);
  const mounted = useRef(true);

  const load = useCallback(async () => {
    const [tc, pg, vs, cr, dv, sh, rp, qd] = await Promise.allSettled([
      // limit=1: this call is only for the totals, page breakdown and
      // generation summary — the table fetches its own rows.
      pageProjectTestCases(projectId, { limit: 1 }),
      getPages(projectId),
      getTestCaseVersions(projectId),
      getCredentials(projectId),
      getProjectAssignees(projectId),
      getTestCaseSheets(projectId),
      getProjectRepositories(projectId),
      // Asked for on every load, so the "Generate N queued" button can exist
      // BEFORE a generation has run in this tab — which is exactly when it is
      // most useful. Best-effort: an older backend has no such route, and a
      // missing count should hide the button rather than break the page.
      getQueuedPages(projectId),
    ]);
    const tcVal = tc.status === "fulfilled" ? tc.value : null;
    setSummary(tcVal);
    setPages(pg.status === "fulfilled" ? pg.value : null);
    setVersions(vs.status === "fulfilled" ? vs.value.versions || [] : []);
    setSheets(sh.status === "fulfilled" ? sh.value.sheets || [] : []);
    const creds = cr.status === "fulfilled" ? cr.value.credentials || [] : [];
    setCredentials(creds);
    setRepos(rp.status === "fulfilled" ? rp.value.repositories || [] : []);
    setQueued(qd.status === "fulfilled" ? qd.value : { total: 0, accounts: [] });
    // Default the single-page login selector to the DEFAULT login (or the
    // first) so the common case needs no extra click.
    if (creds.length) {
      const def = (creds.find((c) => c.is_default) || creds[0]).label || "";
      setPageAccount((prev) => prev || def);
    }
    setRoster(dv.status === "fulfilled" ? dv.value : null);
    if (tcVal) onTotals?.(tcVal.total ?? 0, tcVal.generation || null);
  }, [projectId, onTotals]);

  // ---- push-to-runner ------------------------------------------------------
  const sendToRunner = useCallback(async (target) => {
    setPushing(target);
    try {
      const fn = target === "exe" ? pushToExe : pushToExtension;
      const res = await fn(projectId);
      const where = target === "exe" ? "Exe agent" : "Extension";
      const bits = [`${res.pushed} case${res.pushed === 1 ? "" : "s"} sent to ${where}`];
      if (res.duplicates) bits.push(`${res.duplicates} already there`);
      if (res.skipped) bits.push(`${res.skipped} skipped`);
      toastSuccess(bits.join(" · ") + ".");
    } catch (e) {
      toastError(e.message || `Could not send the test cases.`);
    } finally { setPushing(null); }
  }, [projectId]);

  // LIFECYCLE ONLY. This must have EMPTY deps: `mounted` and the poll timer track
  // how long the component is on screen, not when data changes. Tying them to
  // `load` (whose identity churns whenever the parent's project list re-renders)
  // meant an ordinary refresh cleared the poll timer and flipped `mounted` to
  // false — silently killing progress updates mid-generation.
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      if (polling.current) clearTimeout(polling.current);
    };
  }, []);

  // Initial + reactive data load (safe to re-run whenever `load` changes).
  useEffect(() => {
    (async () => {
      setLoading(true);
      await load();
      setLoading(false);
    })();
  }, [load]);

  // ---- generation ----------------------------------------------------------
  // `load` is read through a ref so the poll chain never has to be rebuilt when
  // the loader's identity changes — a stable poller is what keeps the progress
  // banner alive for the whole run.
  const loadRef = useRef(load);
  useEffect(() => { loadRef.current = load; }, [load]);

  // Live refresh DURING a run, not just after it. Pages/cases are already saved
  // to the database as the run goes (crawl pages once crawling ends, each page's
  // cases the moment it's generated) — the poll used to only re-fetch them once
  // the WHOLE run finished, so a batch that had been sitting done for ten minutes
  // stayed invisible while later batches were still generating. Triggered off two
  // signals already in the workflow payload: `phase` (so the page list appears
  // the moment crawling hands off to generating) and `saved_pages` (so each newly
  // saved page's cases appear without waiting on the pages behind it).
  const lastRefresh = useRef({ phase: null, saved_pages: -1, pages_crawled: -1 });

  const poll = useCallback(() => {
    getWorkflow(projectId).then((w) => {
      if (!mounted.current) return;
      setWorkflow(w);
      if (w.state === "running") {
        const prev = lastRefresh.current;
        // `pages_crawled` climbs DURING crawling itself, one page at a time — the
        // same signal `saved_pages` already is for generation, just for the phase
        // before it. Without this, crawled pages only appeared once crawling
        // finished entirely and `phase` flipped, even though each one is saved to
        // the database (and visible via getPages) the moment it's captured.
        if (w.phase !== prev.phase || (w.saved_pages || 0) > prev.saved_pages
            || (w.pages_crawled || 0) > prev.pages_crawled) {
          lastRefresh.current = { phase: w.phase, saved_pages: w.saved_pages || 0,
                                  pages_crawled: w.pages_crawled || 0 };
          loadRef.current?.();
        }
        polling.current = setTimeout(poll, 2000);
      } else {
        setBusy(false);
        if (w.state === "failed") toastError(w.error || w.message || "Generation failed.");
        loadRef.current?.();
      }
    }).catch(() => {
      // Transient network blip — keep the chain alive rather than dropping it.
      if (mounted.current) polling.current = setTimeout(poll, 3000);
    });
  }, [projectId]);

  // RESUME: generation runs on the SERVER, but `busy`/`workflow` are local state.
  // Without this, leaving the screen and coming back looked idle while a crawl or
  // generate was still going. Ask the server what's actually happening and
  // re-attach the poller — progress now survives navigating away, switching tabs
  // and a full page reload. Keyed on projectId only, so it fires once per project.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const w = await getWorkflow(projectId);
        if (cancelled || !mounted.current || w?.state !== "running") return;
        setWorkflow(w);
        setBusy(true);
        if (polling.current) clearTimeout(polling.current);  // never stack pollers
        poll();
      } catch {
        /* no workflow recorded yet, or unreachable — nothing to resume */
      }
    })();
    return () => { cancelled = true; };
  }, [projectId, poll]);

  const generate = useCallback(async ({ regenerate = false, account } = {}) => {
    if (busy) return;
    if (credentials.length === 0) {
      toastError("Add at least one login before generating.");
      return;
    }
    setBusy(true);
    setWorkflow({ state: "running", phase: "starting", message: account ? `Starting… (${account} only)` : "Starting…" });
    // A fresh run's saved_pages/pages_crawled start back at 0 — without
    // resetting this, the live-refresh check above would compare against the
    // PREVIOUS run's count and never fire until this run caught back up to it.
    lastRefresh.current = { phase: null, saved_pages: -1, pages_crawled: -1 };
    // The OLD crawl's pages disappear the instant Regenerate is clicked, not
    // whenever the first poll happens to land — the backend clears them from
    // the database at the same moment (see clear_account_pages), so this just
    // keeps the screen honest about what's actually there right now.
    if (regenerate) setPages(null);
    try {
      const res = await generateTests(projectId, { regenerate, account });
      if (res?.status === "skipped") {
        setBusy(false);
        setWorkflow(null);
        toastError("Test cases already exist — use Regenerate to rebuild them.");
        return;
      }
      poll();
    } catch (e) {
      setBusy(false);
      setWorkflow(null);
      toastError(e.message || "Could not start generation.");
    }
  }, [busy, credentials.length, projectId, poll]);

  // ---- finish what the last crawl never reached ----------------------------
  // Generate and Regenerate both start at the base URL, rediscover every link and
  // pay the model for every page again. Measured on this project: 52 pages already
  // covered, re-crawled and re-generated for 1.7M tokens to reach 17 new ones.
  // This crawls only what is queued; the pages already covered go down as evidence
  // (read, never generated for, no tokens) and the new cases join the CURRENT
  // version rather than starting another one.
  // How many pages this ONE login still owes a crawl. The queue is scoped by
  // account because an admin's product is not a tester's, so "what is left" is
  // only answerable per login — and the resume run has to sign in as that login
  // to reach those pages at all.
  const queuedFor = useCallback(
    (label) => (queued.accounts || []).find((a) => a.label === label)?.count || 0,
    [queued.accounts]);
  // This login's own interrupted generation (a power cut, not a clean Stop) —
  // separate from queuedFor, which is pages never CRAWLED. A login can have
  // pages that crawled fine but never got test cases when the worker died.
  const resumeCountFor = useCallback(
    (label) => queued.resume_by_account?.[label] || 0,
    [queued.resume_by_account]);

  const generateQueued = useCallback(async (account) => {
    if (busy) return;
    if (credentials.length === 0) {
      toastError("Add at least one login before generating.");
      return;
    }
    const n = account ? queuedFor(account) : queued.total;
    setBusy(true);
    setWorkflow({ state: "running", phase: "starting",
                  message: account
                    ? `Starting on ${n} queued page(s) for ${account}…`
                    : `Starting on ${n} queued page(s)…` });
    lastRefresh.current = { phase: null, saved_pages: -1 };
    try {
      const res = await generateQueuedPages(projectId, { account });
      if (res?.status === "skipped") {
        // Someone finished them between the render and the click.
        setBusy(false);
        setWorkflow(null);
        setQueued({ total: 0, accounts: [] });
        toastSuccess("Nothing is queued — every page found has already been covered.");
        return;
      }
      poll();
    } catch (e) {
      setBusy(false);
      setWorkflow(null);
      toastError(e.message || "Could not start on the queued pages.");
    }
  }, [busy, credentials.length, projectId, queued.total, queuedFor, poll]);

  // ---- pause / resume / stop ----------------------------------------------
  // All three are cooperative: whatever is mid-flight finishes, then the workers
  // either park (pause) or exit (stop). The optimistic flag keeps the button
  // state honest between the click and the next 2s poll.
  const [controlBusy, setControlBusy] = useState(false);

  const togglePause = useCallback(async (shouldPause) => {
    setControlBusy(true);
    try {
      await (shouldPause ? pauseProject(projectId) : resumeProject(projectId));
      setWorkflow((w) => (w ? { ...w, paused: shouldPause } : w));
    } catch (e) {
      toastError(e.message || (shouldPause ? "Could not pause." : "Could not resume."));
    } finally { setControlBusy(false); }
  }, [projectId]);

  const stop = useCallback(async () => {
    setControlBusy(true);
    try {
      await cancelProject(projectId);
      // Un-pause too, otherwise parked workers would never wake to see the stop.
      await resumeProject(projectId).catch(() => {});
      setWorkflow((w) => (w ? { ...w, paused: false, message: "Stopping…" } : w));
      toastSuccess("Stopping — the page in progress will finish first.");
    } catch (e) {
      toastError(e.message || "Could not stop.");
    } finally { setControlBusy(false); }
  }, [projectId]);

  const download = useCallback(async (version) => {
    setDownloading(true);
    try {
      if (version) await downloadTestCasesVersion(projectId, version);
      else await downloadProjectTestCases(projectId);
    } catch (e) {
      toastError(e.message || "Download failed.");
    } finally {
      setDownloading(false);
    }
  }, [projectId]);

  // ---- single-page generate ------------------------------------------------
  const generatePage = useCallback(async () => {
    if (busy) return;
    const url = pageUrl.trim();
    if (!url) { toastError("Enter the URL of the page you want to test."); return; }
    if (!pageAccount) { toastError("Pick a login to reach the page with."); return; }
    setBusy(true);
    setWorkflow({ state: "running", phase: "starting", message: `Opening the page as ${pageAccount}…` });
    try {
      await generateTestsForPage(projectId, { url, account: pageAccount });
      poll();
    } catch (e) {
      setBusy(false);
      setWorkflow(null);
      toastError(e.message || "Could not start generation for this page.");
    }
  }, [busy, pageUrl, pageAccount, projectId, poll]);

  // Download just THIS page's test cases. Generating for one page appends its
  // cases to a suite that may hold hundreds, so the project-wide download is no
  // way to read what this card just produced.
  const downloadPage = useCallback(async () => {
    const url = pageUrl.trim();
    if (!url) { toastError("Enter the URL of the page whose cases you want."); return; }
    setPageDl(true);
    try {
      await downloadProjectTestCases(projectId, { pageUrl: url });
    } catch (e) {
      toastError(e.message || `No test cases for ${url} yet — generate them first.`);
    } finally {
      setPageDl(false);
    }
  }, [pageUrl, projectId]);

  // Send just THIS page's cases to the exe runner. Scoped like the download
  // beside it: pushing the whole project to re-run one new page is what this
  // card exists to avoid.
  const sendPageToExe = useCallback(async () => {
    const url = pageUrl.trim();
    if (!url) { toastError("Enter the URL of the page whose cases you want to send."); return; }
    setPageSend(true);
    try {
      const res = await pushToExe(projectId, { pageUrl: url });
      const bits = [`${res.pushed} case${res.pushed === 1 ? "" : "s"} sent to Exe agent`];
      if (res.duplicates) bits.push(`${res.duplicates} already there`);
      if (res.skipped) bits.push(`${res.skipped} skipped`);
      toastSuccess(bits.join(" · ") + ".");
    } catch (e) {
      toastError(e.message || `No test cases for ${url} yet — generate them first.`);
    } finally {
      setPageSend(false);
    }
  }, [pageUrl, projectId]);

  // Download just this login's test cases (per-account, from its row).
  const downloadForAccount = useCallback(async (label) => {
    setRowDl(label);
    try {
      await downloadProjectTestCases(projectId, { account: label });
    } catch (e) {
      toastError(e.message || `No test cases for “${label}” yet — generate them first.`);
    } finally {
      setRowDl(null);
    }
  }, [projectId]);

  const rollback = useCallback(async (version) => {
    try {
      await rollbackTestCases(projectId, version);
      await load();
      toastSuccess(`Restored v${version}.`);
    } catch (e) {
      toastError(e.message || "Rollback failed.");
    }
  }, [projectId, load]);

  // ---- credentials ---------------------------------------------------------
  const openAddCred = () => {
    setCredEditId(null);
    setCredForm({ ...EMPTY_CRED, is_default: credentials.length === 0 });
    setCredError(null);
    setCredOpen(true);
  };
  const openEditCred = (c) => {
    setCredEditId(c.id);
    setCredForm({
      label: c.label || "",
      role: c.role || "",
      login_url: c.login_url || "",
      username: c.username || "",
      password: "",
      is_default: !!c.is_default,
      username_field_override_enabled: !!c.username_field_override_enabled,
      username_field_override: c.username_field_override || "",
    });
    setCredError(null);
    setCredOpen(true);
  };

  const saveCred = useCallback(async () => {
    const f = credForm;
    if (!f.label.trim()) { setCredError("A label (e.g. Admin) is required."); return; }
    if (!f.username.trim()) { setCredError("Username / email is required."); return; }
    if (!credEditId && !f.password) { setCredError("Password is required for a new login."); return; }
    setCredSaving(true);
    setCredError(null);
    try {
      const payload = {
        label: f.label.trim(),
        role: f.role.trim() || undefined,
        login_url: f.login_url.trim() || undefined,
        username: f.username.trim(),
        is_default: f.is_default,
        username_field_override_enabled: !!f.username_field_override_enabled,
        username_field_override:
          f.username_field_override_enabled && f.username_field_override
            ? f.username_field_override : undefined,
      };
      if (f.password) payload.password = f.password; // omit → keep existing
      if (credEditId) await updateCredential(projectId, credEditId, payload);
      else await addCredential(projectId, payload);
      setCredOpen(false);
      await load();
    } catch (e) {
      setCredError(e.message || "Could not save the login.");
    } finally {
      setCredSaving(false);
    }
  }, [credForm, credEditId, projectId, load]);

  const removeCred = useCallback(async (credId) => {
    setCredentials((prev) => prev.filter((c) => c.id !== credId)); // optimistic
    try {
      await deleteCredential(projectId, credId);
    } catch (e) {
      toastError(e.message || "Could not delete the login.");
    } finally {
      await load();
    }
  }, [projectId, load]);

  const removeProject = useCallback(async () => {
    setDeleting(true);
    try {
      await deleteProject(projectId);
      toastSuccess("Project deleted.");
      onDeleted?.();
    } catch (e) {
      toastError(e.message || "Could not delete the project.");
      setDeleting(false);
    }
  }, [projectId, onDeleted]);

  // ---- derived -------------------------------------------------------------
  // How many cases a LOGIN already owns, counting every company behind it: one
  // email can be registered to several companies, and the crawl files each under
  // "<login> › <company>". Counting only the exact label would report zero for a
  // multi-tenant login and offer "Generate" on a suite that already exists.
  const casesForLogin = useCallback((label) => {
    const want = String(label || "").trim().toLowerCase();
    if (!want) return 0;
    return (sheets || []).reduce((sum, s) => {
      const key = String(s.key || s.name || "").trim().toLowerCase();
      const mine = key === want || key.startsWith(`${want} › `);
      return mine ? sum + (s.count || 0) : sum;
    }, 0);
  }, [sheets]);
  const total = summary?.total ?? 0;
  const gen = summary?.generation || null;
  const byPage = summary?.by_page || [];
  const pagesCount = pages?.pages_found ?? byPage.length ?? 0;
  // Generating needs BOTH a way in and something to read: a login to reach the
  // app behind its auth wall, and a repository whose routes and forms tell us
  // what the pages are meant to do. Without either, generation either produces
  // nothing or produces guesses — so the button is hidden rather than offered
  // and then rejected. The legacy repository_url counts: projects created before
  // the repositories table have no row but are still connected.
  const hasRepo = repos.length > 0 || !!project?.repository_url;
  const missing = [
    credentials.length === 0 && "a login",
    !hasRepo && "a repository",
  ].filter(Boolean);
  const ready = missing.length === 0;
  const canGenerate = ready && !busy;
  const paused = !!workflow?.paused;

  const name = project?.name || summary?.name || "Project";
  const baseUrl = project?.base_url || "";

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center text-slate-400">
        <Loader2 className="animate-spin" /> <span className="ml-2">Loading project…</span>
      </div>
    );
  }

  return (
    <>
      <button onClick={onBack} className="mb-4 inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-brand-600 dark:text-slate-400">
        <ArrowLeft size={16} /> All projects
      </button>

      {/* summary strip */}
      <motion.div
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45, ease: "easeOut" }}
        className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-6 shadow-soft dark:border-white/10 dark:bg-ink-900"
      >
        <Aurora className="opacity-50" />
        <div className="relative flex flex-col gap-5 xl:flex-row xl:items-start xl:justify-between">
          <div className="flex items-start gap-3">
            <span className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-gradient-to-br from-brand-500 to-fuchsia-500 text-white shadow-glow">
              <FolderKanban size={22} />
            </span>
            <div className="min-w-0">
              <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900 dark:text-white">{name}</h1>
              {baseUrl && (
                <p className="mt-1 flex items-center gap-1.5 text-sm text-slate-500 dark:text-slate-400">
                  <Globe size={14} className="shrink-0" /> <span className="truncate">{baseUrl}</span>
                </p>
              )}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {ready ? (
              <>
                <Button size="sm" onClick={() => generate({ regenerate: false })} disabled={!canGenerate}>
                  {busy ? <><Loader2 size={15} className="animate-spin" /> Generating…</> : <><Sparkles size={15} /> Generate</>}
                </Button>
                <Button variant="outline" size="sm" onClick={() => generate({ regenerate: true })} disabled={!canGenerate || total === 0}
                  title="Build a fresh set as the next version. The current set is archived, not deleted — you can download or restore it from Version history.">
                  <RefreshCw size={15} /> Regenerate
                </Button>
                {queued.total > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => generateQueued()}
                    disabled={!canGenerate}
                    title={`Crawl and generate ONLY the ${queued.total} page(s) the last run never reached`
                      + (queued.accounts.length > 1
                          ? ` — ${queued.accounts.map((a) => `${a.label || "signed out"}: ${a.count}`).join(", ")}`
                          : "")
                      + ". The pages already covered are not re-crawled or re-generated, and the current version is added to rather than replaced."}
                  >
                    <ListPlus size={15} /> Generate {queued.total} queued
                  </Button>
                )}
                {/* Nothing left to CRAWL, but a worker killed mid-batch (a power
                    cut, not a clean Stop) can leave generation itself unfinished
                    on pages that crawled fine — crawl_queue has no idea, so this
                    is a SEPARATE signal from the button above. */}
                {queued.total === 0 && queued.resuming_generation && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => generateQueued()}
                    disabled={!canGenerate}
                    title={`A previous run was interrupted before finishing — resume generating `
                      + `the ${queued.resume_count} page(s) it never reached. Pages already `
                      + "generated are not redone."}
                  >
                    <ListPlus size={15} /> Resume generation ({queued.resume_count})
                  </Button>
                )}
              </>
            ) : (
              // Stands where Generate would be, so the reason it's gone is read
              // in the same glance that looked for the button.
              <MissingGate missing={missing} />
            )}
            <Button variant="outline" size="sm" onClick={() => download()} disabled={downloading || total === 0}>
              {downloading ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />} Excel
            </Button>
            {/* Push the generated cases straight into a runner, TAGGED with this
                project. That tag is what lets the batch report be traced back
                here — it's what the assigned developer's dashboard reads. */}
            <Button variant="outline" size="sm" onClick={() => sendToRunner("exe")}
              disabled={pushing || total === 0}
              title="Copy these test cases into the Exe agent, tagged with this project">
              {pushing === "exe" ? <Loader2 size={15} className="animate-spin" /> : <Bot size={15} />} Send to Exe
            </Button>
            <Button variant="outline" size="sm" onClick={() => sendToRunner("extension")}
              disabled={pushing || total === 0}
              title="Copy these test cases into the Extension, tagged with this project">
              {pushing === "extension" ? <Loader2 size={15} className="animate-spin" /> : <Puzzle size={15} />} Send to Extension
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setConfirmDel(true)}
              className="!text-rose-600 hover:!bg-rose-50 dark:!text-rose-300 dark:hover:!bg-rose-500/10">
              <Trash2 size={15} /> Delete
            </Button>
          </div>
        </div>

        {/* stats */}
        <div className="relative mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
          {[
            { icon: Layers, label: "Pages", value: pagesCount, tone: "text-brand-500 bg-brand-500/10" },
            { icon: FileText, label: "Test cases", value: total, tone: "text-sky-500 bg-sky-500/10" },
            { icon: DollarSign, label: "Gen cost", value: gen ? fmtCost(gen.cost_usd) : "—", tone: "text-emerald-500 bg-emerald-500/10" },
            { icon: Clock, label: "Gen time", value: gen ? fmtTime(gen.duration_seconds) : "—", tone: "text-violet-500 bg-violet-500/10" },
          ].map((s) => (
            <div key={s.label} className="flex items-center gap-3 rounded-xl border border-slate-100 bg-slate-50/70 px-4 py-3 dark:border-white/5 dark:bg-white/5">
              <span className={cn("grid h-9 w-9 shrink-0 place-items-center rounded-lg", s.tone)}><s.icon size={16} /></span>
              <div className="min-w-0">
                <p className="truncate text-xs text-slate-400">{s.label}</p>
                <p className="text-lg font-bold text-slate-900 dark:text-white">{s.value}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Workbook tabs — what the Excel download will be split into, in sheet
            order. Includes Public and Login & Registration, which are not
            credentials and so never show up in the per-login rows below. Each
            chip downloads just that sheet. */}
        {sheets.length > 0 && (
          <div className="mt-5">
            <p className="mb-2 text-xs font-medium text-slate-500 dark:text-slate-400">
              Excel tabs ({sheets.length + 1}) — Summary
              {sheets.map((s) => ` · ${s.name}`).join("")}
            </p>
            <div className="flex flex-wrap gap-2">
              {sheets.map((s) => (
                <button
                  key={s.name}
                  onClick={() => downloadForAccount(s.key || s.name)}
                  disabled={rowDl === (s.key || s.name)}
                  title={`Download the “${s.name}” sheet · ${s.pages} page${s.pages === 1 ? "" : "s"}, ${s.modules} module${s.modules === 1 ? "" : "s"}`}
                  className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-medium text-slate-600 transition hover:border-brand-300 hover:text-brand-600 disabled:opacity-50 dark:border-white/10 dark:bg-white/5 dark:text-slate-300 dark:hover:text-brand-300"
                >
                  {rowDl === (s.key || s.name)
                    ? <Loader2 size={12} className="animate-spin" />
                    : <Download size={12} className="opacity-60" />}
                  {s.name}
                  <span className="text-slate-400">{s.count}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </motion.div>

      {/* live progress */}
      {busy && workflow && (
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
          className={cn(
            "mt-5 overflow-hidden rounded-2xl border px-4 py-3",
            paused
              ? "border-amber-200 bg-amber-50/70 dark:border-amber-500/20 dark:bg-amber-500/10"
              : "border-brand-200 bg-brand-50/70 dark:border-brand-500/20 dark:bg-brand-500/10",
          )}>
          <div className="flex flex-wrap items-center gap-3">
            <div className={cn("flex min-w-0 flex-1 items-center gap-3 text-sm font-medium",
              paused ? "text-amber-800 dark:text-amber-200" : "text-brand-700 dark:text-brand-200")}>
              {paused ? <Pause size={17} /> : <Loader2 size={17} className="animate-spin" />}
              <span className="capitalize">{paused ? "Paused" : workflow.phase}</span>
              {paused
                ? <span className="text-amber-600 dark:text-amber-300/80">
                    — nothing is lost; Resume carries on from here
                  </span>
                : workflow.message && <span className="text-brand-400">— {workflow.message}</span>}
            </div>
            {/* Pause / Resume / Stop. All cooperative: whatever is mid-flight
                finishes first, so a snapshot is never left half-captured. */}
            <div className="flex shrink-0 items-center gap-2">
              <Button variant="outline" size="sm" disabled={controlBusy}
                onClick={() => togglePause(!paused)}
                title={paused ? "Carry on from where it stopped" : "Finish the current page, then wait"}>
                {controlBusy
                  ? <Loader2 size={14} className="animate-spin" />
                  : paused ? <Play size={14} /> : <Pause size={14} />}
                {paused ? "Resume" : "Pause"}
              </Button>
              <Button variant="outline" size="sm" onClick={stop} disabled={controlBusy}
                title="Stop this run — pages found so far are discarded"
                className="!border-rose-300 !text-rose-600 hover:!bg-rose-50 dark:!border-rose-500/40 dark:!text-rose-300 dark:hover:!bg-rose-500/10">
                <Square size={14} /> Stop
              </Button>
            </div>
          </div>
          <div className={cn("mt-2.5 h-1 w-full overflow-hidden rounded-full",
            paused ? "bg-amber-200/60 dark:bg-amber-500/20" : "bg-brand-200/60 dark:bg-brand-500/20")}>
            {paused ? (
              <div className="h-full w-1/3 rounded-full bg-amber-400/70" />
            ) : (
              <motion.div className="h-full w-1/3 rounded-full bg-gradient-to-r from-brand-500 to-fuchsia-500"
                animate={{ x: ["-110%", "330%"] }} transition={{ repeat: Infinity, duration: 1.4, ease: "easeInOut" }} />
            )}
          </div>
          {/* per-account crawl coverage */}
          {workflow?.accounts?.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {workflow.accounts.map((a) => {
                const broken = !a.skipped && a.pages === 0;
                return (
                  <span key={a.label} className={cn("rounded-full px-2.5 py-1 text-xs font-medium",
                    a.skipped ? "bg-slate-100 text-slate-500 dark:bg-white/5 dark:text-slate-400"
                    : broken ? "bg-rose-50 text-rose-600 dark:bg-rose-500/10 dark:text-rose-400"
                    : "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400")}>
                    {a.label}: {a.skipped ? "skipped" : `${a.pages} page${a.pages === 1 ? "" : "s"}`}
                    {broken ? " · login failed?" : ""}
                  </span>
                );
              })}
            </div>
          )}
          {/* per-batch progress — visible during "generating" once the first
              batch is frozen; the array carries every batch including terminal
              ones, so a finished batch never disappears as later ones run. */}
          <BatchProgress
            batches={workflow.batches}
            currentBatch={workflow.current_batch}
            totalBatches={workflow.total_batches}
          />
        </motion.div>
      )}

      {/* Every crawled page, by name — not just the ones with cases so far.
          Shown as soon as pages exist, so a run's inventory is visible during
          crawling/generating instead of only once the whole run finishes. */}
      <div className="mt-6">
        <CrawledPagesList pages={pages?.pages} byPage={byPage} busy={busy} />
      </div>

      {/* logins gate */}
      <motion.div
        initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.45, ease: "easeOut" }}
        className="mt-6 card overflow-hidden"
      >
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 px-5 py-4 dark:border-white/10">
          <div>
            <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-800 dark:text-white">
              <KeyRound size={16} className="text-brand-500" /> Role-based logins
              <span className="text-slate-400">({credentials.length})</span>
            </h3>
            <p className="mt-0.5 text-xs text-slate-400">
              Add a login per role (Admin, User, …). Generation stays locked until at least one is saved.
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={openAddCred}><Plus size={14} /> Add login</Button>
        </div>

        {credentials.length === 0 ? (
          <div className="flex flex-col items-center gap-2 px-5 py-8 text-center">
            <ShieldAlert size={26} className="text-amber-500" />
            <p className="text-sm text-slate-600 dark:text-slate-300">No logins yet — add one to unlock <b>Generate</b>.</p>
            <Button size="sm" onClick={openAddCred}><Plus size={14} /> Add your first login</Button>
          </div>
        ) : (
          <ul className="divide-y divide-slate-100 dark:divide-white/5">
            {credentials.map((c) => (
              <li key={c.id} className="flex flex-wrap items-center justify-between gap-3 px-5 py-3">
                <div className="min-w-0">
                  <p className="flex flex-wrap items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-200">
                    {c.label}
                    {c.role && <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[0.625rem] uppercase text-slate-500 dark:bg-white/10 dark:text-slate-300">{c.role}</span>}
                    {c.is_default && <span className="rounded bg-brand-500/10 px-1.5 py-0.5 text-[0.625rem] uppercase text-brand-600 dark:text-brand-300">default</span>}
                    {/* Which input the login actually signs in with. Learned on the
                        first successful login, so it's a read-only fact here, not a
                        setting. Absent until this credential has logged in once. */}
                    {c.username_field_override && (
                      <span
                        title={c.username_field_override_enabled
                          ? "You pinned this field for the username"
                          : "Detected automatically on the last successful login"}
                        className="inline-flex items-center gap-1 rounded bg-slate-100 px-1.5 py-0.5 text-[0.625rem] text-slate-500 dark:bg-white/10 dark:text-slate-300"
                      >
                        <AtSign size={10} /> {c.username_field_override}
                        {!c.username_field_override_enabled && (
                          <span className="text-slate-400">· auto</span>
                        )}
                      </span>
                    )}
                  </p>
                  <p className="truncate text-xs text-slate-400">
                    {c.username} · •••••• {c.login_url ? `· ${c.login_url}` : ""}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {/* Same gate as the header button — a per-login generate is
                      still a generate, and would hit the same missing repo. */}
                  {/* One button, not two. A per-login run REPLACES that login's
                      cases whether or not `regenerate` is passed — the server
                      checks `account` before `regenerate` — so a separate
                      "Regenerate" would be the same request under a second name.
                      The label says which of the two it is about to do. */}
                  {ready && (() => {
                    const owned = casesForLogin(c.label);
                    return (
                      <Button size="sm" variant="outline"
                        onClick={() => generate({ account: c.label })}
                        disabled={busy}
                        title={owned
                          ? `Re-crawl this login and REPLACE its ${owned} existing `
                            + `case${owned === 1 ? "" : "s"} — every company behind it, `
                            + `and no other login`
                          : "Crawl & generate for this login only"}>
                        {owned
                          ? <><RefreshCw size={14} /> Regenerate</>
                          : <><Sparkles size={14} /> Generate</>}
                      </Button>
                    );
                  })()}
                  {/* This login's own leftovers. Beside its Regenerate, because the
                      two are the same choice at different prices: Regenerate
                      re-crawls and re-pays for every page this login already has,
                      this one only finishes the pages it never reached. */}
                  {ready && queuedFor(c.label) > 0 && (
                    <Button size="sm" variant="outline"
                      onClick={() => generateQueued(c.label)}
                      disabled={busy}
                      title={`Crawl and generate ONLY the ${queuedFor(c.label)} page(s) `
                        + `this login never reached. Its existing cases are kept and `
                        + `added to — nothing is re-crawled or re-paid for.`}>
                      <ListPlus size={14} /> {queuedFor(c.label)} queued
                    </Button>
                  )}
                  {/* Nothing left to CRAWL for this login, but a worker killed
                      mid-batch (a power cut) can leave ITS generation unfinished
                      on pages that crawled fine. Same action (generateQueued),
                      the backend routes it to the right resume automatically. */}
                  {ready && queuedFor(c.label) === 0 && resumeCountFor(c.label) > 0 && (
                    <Button size="sm" variant="outline"
                      onClick={() => generateQueued(c.label)}
                      disabled={busy}
                      title={`This login's last generation was interrupted before finishing `
                        + `— resume the ${resumeCountFor(c.label)} page(s) it never reached. `
                        + `Pages already generated are not redone.`}>
                      <ListPlus size={14} /> Resume ({resumeCountFor(c.label)})
                    </Button>
                  )}
                  <Button size="sm" variant="outline" onClick={() => downloadForAccount(c.label)}
                    disabled={rowDl === c.label} title="Download this login's test cases (Excel)">
                    {rowDl === c.label ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />} Download
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => openEditCred(c)}><Pencil size={14} /></Button>
                  <Button size="sm" variant="ghost" onClick={() => removeCred(c.id)}
                    className="!text-rose-600 hover:!bg-rose-50 dark:!text-rose-300 dark:hover:!bg-rose-500/10"><Trash2 size={14} /></Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </motion.div>

      {/* single page / tab generate */}
      <motion.div
        initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.45, ease: "easeOut" }}
        className="mt-6 card overflow-hidden"
      >
        <div className="border-b border-slate-200 px-5 py-4 dark:border-white/10">
          <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-800 dark:text-white">
            <FilePlus2 size={16} className="text-brand-500" /> Test a single page
          </h3>
          <p className="mt-0.5 text-xs text-slate-400">
            Added a new tab or page? Paste its URL, pick which login can reach it, and
            generate test cases for just that page — they&apos;re appended to the suite, so
            you don&apos;t re-run (or re-pay for) the whole project.
          </p>
        </div>

        <div className="px-5 py-4">
          {!ready ? (
            <MissingGate missing={missing} />
          ) : (
            <>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <div className="flex-1">
                  <Input
                    icon={Globe}
                    value={pageUrl}
                    onChange={(e) => setPageUrl(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") generatePage(); }}
                    placeholder="https://yourapp.com/new-page"
                    disabled={busy}
                  />
                </div>
                <select
                  value={pageAccount}
                  disabled={busy}
                  onChange={(e) => setPageAccount(e.target.value)}
                  className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-medium text-slate-600 dark:border-white/10 dark:bg-white/5 dark:text-slate-300"
                >
                  {credentials.map((c) => (
                    <option key={c.id} value={c.label}>{c.label}</option>
                  ))}
                </select>
                <Button onClick={generatePage} disabled={busy || !pageUrl.trim()}>
                  {busy ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />}
                  Generate for this page
                </Button>
                {/* Scoped to the URL above, not the whole project: these cases are
                    appended to a suite that may hold hundreds, so the project-wide
                    download is no way to read what this card just produced. */}
                <Button
                  variant="outline"
                  onClick={downloadPage}
                  disabled={busy || pageDl || !pageUrl.trim()}
                  title="Download the test cases for this page only"
                >
                  {pageDl ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />}
                  Excel
                </Button>
                <Button
                  variant="outline"
                  onClick={sendPageToExe}
                  disabled={busy || pageSend || !pageUrl.trim()}
                  title="Copy this page's test cases into the Exe agent, tagged with this project"
                >
                  {pageSend ? <Loader2 size={15} className="animate-spin" /> : <Bot size={15} />}
                  Send to Exe
                </Button>
              </div>

            </>
          )}
        </div>
      </motion.div>

      {/* repository — moved here from the create modal. The PM defines the
          project; the tester connects the codebase, because they're the one who
          knows which repo backs the app. */}
      <motion.div
        initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.45, ease: "easeOut" }}
        className="mt-6"
      >
        <RepositoryConnections
          project={project}
          projectId={projectId}
          onSaved={() => { load(); onSaved?.(); }}
        />
      </motion.div>

      {/* who else is on this project — read-only. Staffing is the PM's call, so
          this answers "who fixes what I find?" without offering to change it. */}
      {roster && (roster.assigned_developers?.length > 0 || roster.assigned_testers?.length > 1) && (
        <motion.div
          initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.45, ease: "easeOut" }}
          className="mt-6 card overflow-hidden"
        >
          <div className="border-b border-slate-200 px-5 py-4 dark:border-white/10">
            <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-800 dark:text-white">
              <Code2 size={16} className="text-emerald-500" /> Who else is on this project
            </h3>
            <p className="mt-0.5 text-xs text-slate-400">
              Assigned developers get the report by email when a batch finishes, then mark
              what they&apos;ve fixed. Your project manager decides who&apos;s on the team.
            </p>
          </div>
          <ul className="divide-y divide-slate-100 dark:divide-white/5">
            {[
              ...(roster.assigned_testers || []).map((d) => ({ ...d, kind: "tester" })),
              ...(roster.assigned_developers || []).map((d) => ({ ...d, kind: "developer" })),
            ].map((d) => (
              <li key={d.id} className="flex flex-wrap items-center justify-between gap-3 px-5 py-3">
                <div className="flex min-w-0 items-center gap-2.5">
                  <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 text-[0.6875rem] font-bold text-white">
                    {(d.name || d.email || "?").split(/[\s@.]+/).map((x) => x[0]).slice(0, 2).join("").toUpperCase()}
                  </span>
                  <div className="min-w-0">
                    <p className="flex items-center gap-2 truncate text-sm font-medium text-slate-700 dark:text-slate-200">
                      {d.name || (d.email || "").split("@")[0]}
                      {d.invite_status === "pending" && (
                        <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[0.625rem] font-semibold uppercase text-amber-700 dark:bg-amber-500/15 dark:text-amber-300">
                          pending
                        </span>
                      )}
                    </p>
                    <p className="truncate text-xs text-slate-400">{d.email}</p>
                  </div>
                </div>
                <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-[0.6875rem] font-semibold capitalize text-slate-600 dark:bg-white/10 dark:text-slate-300">
                  {d.kind}
                </span>
              </li>
            ))}
          </ul>
        </motion.div>
      )}

      {/* Pages -> cases -> one case, with per-page send buttons. Lives in its
          own component: this file is already long, and the drill-down carries
          enough state of its own to be worth keeping separate. */}
      {total > 0 ? (
        <motion.div
          initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.45, ease: "easeOut" }}
          className="mt-6"
        >
          <ProjectCasesCard
            projectId={projectId}
            total={total}
            byPage={byPage}
            generation={gen}
          />
        </motion.div>
      ) : (
        !busy && ready && (
          <div className="mt-6">
            <EmptyState
              icon={FolderSearch}
              title="No test cases yet"
              message="Generate to crawl this app's pages and auto-create a suite for your logins."
              action={<Button onClick={() => generate({ regenerate: false })}><Sparkles size={16} /> Generate</Button>}
            />
          </div>
        )
      )}

      {/* Version history. Shown from the FIRST version, not the second: gating it
          on a second version meant nobody learned versions existed until after
          they had already regenerated once. */}
      {versions.length > 0 && (
        <div className="mt-6 card overflow-hidden">
          <div className="border-b border-slate-200 px-5 py-4 dark:border-white/10">
            <h3 className="text-sm font-semibold text-slate-800 dark:text-white">
              Version history <span className="text-slate-400">({versions.length})</span>
            </h3>
            <p className="mt-0.5 text-xs text-slate-400">Regeneration keeps every prior set. Restore or download any version.</p>
          </div>
          <div className="divide-y divide-slate-100 dark:divide-white/5">
            {versions.map((v) => (
              <div key={v.version} className="flex flex-wrap items-center justify-between gap-3 px-5 py-3">
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <span className="font-medium text-slate-700 dark:text-slate-200">v{v.version}</span>
                  <span className="text-slate-400">· {v.count} case{v.count === 1 ? "" : "s"}</span>
                  {(v.cost_usd > 0 || v.duration_seconds > 0) && (
                    <span className="text-xs text-slate-400">· {fmtCost(v.cost_usd)} · {fmtTime(v.duration_seconds)}</span>
                  )}
                  {v.is_current && <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-600 dark:bg-emerald-500/10 dark:text-emerald-400">current</span>}
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => download(v.version)}>Download</Button>
                  {!v.is_current && <Button variant="outline" size="sm" onClick={() => rollback(v.version)}>Restore</Button>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* add / edit login modal */}
      <Modal
        open={credOpen}
        onClose={() => setCredOpen(false)}
        title={credEditId ? "Edit login" : "Add login"}
        size="md"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => setCredOpen(false)}>Cancel</Button>
            <Button size="sm" onClick={saveCred} disabled={credSaving}>
              {credSaving ? <><Loader2 size={15} className="animate-spin" /> Saving…</> : "Save login"}
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Input label="Label" placeholder="Admin" value={credForm.label}
              onChange={(e) => setCredForm((f) => ({ ...f, label: e.target.value }))} />
            <Input label="Role (optional)" placeholder="admin / user" value={credForm.role}
              onChange={(e) => setCredForm((f) => ({ ...f, role: e.target.value }))} />
          </div>
          <Input label="Login URL (optional)" icon={Globe} placeholder="https://your-app.com/login" value={credForm.login_url}
            onChange={(e) => setCredForm((f) => ({ ...f, login_url: e.target.value }))} />
          <Input label="Username / email" value={credForm.username}
            onChange={(e) => setCredForm((f) => ({ ...f, username: e.target.value }))} />
          <Input label={credEditId ? "Password (leave blank to keep current)" : "Password"} type="password" value={credForm.password}
            onChange={(e) => setCredForm((f) => ({ ...f, password: e.target.value }))} />

          <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
            <input type="checkbox" checked={credForm.is_default}
              onChange={(e) => setCredForm((f) => ({ ...f, is_default: e.target.checked }))}
              className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500" />
            Use as the default login (covers pages with no specific role)
          </label>

          {/* The login identifier field is detected automatically now — the engine
              ranks the inputs on the sign-in form, tries the best, falls back to
              the next if it doesn't take, and remembers what worked. Nothing to
              configure, so there's no field here any more. */}
          <p className="flex items-start gap-1.5 rounded-xl border border-slate-200 bg-slate-50/60 p-3 text-xs text-slate-500 dark:border-white/10 dark:bg-white/5 dark:text-slate-400">
            <Wand2 size={14} className="mt-0.5 shrink-0 text-brand-500" />
            We work out which field is the username automatically — including forms that
            sign in with a mobile number or an employee ID — and remember it for next time.
          </p>

          <p className="text-xs text-slate-400">The password is encrypted at rest and never shown again or sent to the AI model.</p>
          {credError && (
            <p className="rounded-lg border border-rose-200 bg-rose-50/70 px-3 py-2 text-xs font-medium text-rose-700 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-200">{credError}</p>
          )}
        </div>
      </Modal>

      {/* delete project modal */}
      <Modal
        open={confirmDel}
        onClose={() => setConfirmDel(false)}
        title="Delete project?"
        size="md"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => setConfirmDel(false)}>Cancel</Button>
            <Button size="sm" variant="danger" onClick={removeProject} disabled={deleting}>
              {deleting ? <><Loader2 size={15} className="animate-spin" /> Deleting…</> : <><Trash2 size={15} /> Delete</>}
            </Button>
          </div>
        }
      >
        <div className="flex items-start gap-3">
          <AlertTriangle className="mt-0.5 shrink-0 text-amber-500" size={18} />
          <p className="text-sm text-slate-600 dark:text-slate-300">
            This permanently deletes <b>{name}</b> along with its test cases, crawl results and logins. This cannot be undone.
          </p>
        </div>
      </Modal>
    </>
  );
}
